'use client';

import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import AppLogo from '@/components/ui/AppLogo';
import { Shield, Star, Users, Loader2, TrendingUp } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { getRoleHome } from '@/lib/auth/roles';
import { createClient } from '@/lib/supabase/client';

interface PublicStats {
  active_members: number;
  network_value: number;
}

export default function AuthScreen() {
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const { loading, authRole, authState } = useAuth();
  const router = useRouter();
  const [publicStats, setPublicStats] = useState<PublicStats | null>(null);

  // Load real DB-backed public stats
  useEffect(() => {
    const supabase = createClient();
    supabase.rpc('get_live_public_stats').then(({ data }) => {
      if (data) {
        setPublicStats({
          active_members: data.active_members ?? 0,
          network_value: data.network_value ?? 0,
        });
      }
    }).catch(() => {
      // If RPC not yet available, show zeros
      setPublicStats({ active_members: 0, network_value: 0 });
    });
  }, []);

  // Already authenticated — send user to their portal
  useEffect(() => {
    if (loading) return;
    if (!authRole) return;
    if (
      authState === 'UNAUTHENTICATED' ||
      authState === 'AUTH_INITIALISING' ||
      authState === 'SESSION_RESOLVING'
    ) return;
    router?.replace(getRoleHome(authRole));
  }, [loading, authRole, authState, router]);

  if (loading || authState === 'AUTH_INITIALISING' || authState === 'SESSION_RESOLVING') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-gold animate-spin" />
          <p className="text-sm text-muted-foreground">Checking session...</p>
        </div>
      </div>
    );
  }

  if (authRole && authState !== 'UNAUTHENTICATED') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-gold animate-spin" />
          <p className="text-sm text-muted-foreground">Redirecting to your portal...</p>
        </div>
      </div>
    );
  }

  const formatNetworkValue = (val: number) => {
    if (val >= 1_000_000) return `R${(val / 1_000_000).toFixed(1)}M`;
    if (val >= 1_000) return `R${(val / 1_000).toFixed(1)}K`;
    return `R${val.toLocaleString('en-ZA')}`;
  };

  return (
    <div className="min-h-screen bg-background pattern-grid flex">
      {/* Left Panel — Brand */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 xl:w-3/5 relative overflow-hidden p-12">
        <div className="absolute inset-0 blob-gold opacity-60 pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-96 h-96 blob-gold opacity-40 pointer-events-none" />

        {/* Logo */}
        <div className="flex items-center gap-3 relative z-10">
          <AppLogo size={44} />
          <div>
            <span className="font-bold text-2xl text-foreground tracking-tight block">MBG Nexus</span>
            <span className="text-xs text-muted-foreground">Digital Community Platform</span>
          </div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-lg">
          <p className="text-xs font-semibold text-gold uppercase tracking-widest mb-4">MBG Group — Durban, South Africa</p>
          <h1 className="text-hero-xl gold-text-gradient mb-6">
            Community. Growth. Opportunity.
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed mb-6">
            MBG Nexus is a digital community participation platform inspired by the principles of the traditional stokvel — designed to connect members through structured participation, collective growth and transparent digital administration.
          </p>
          <p className="text-xs text-muted-foreground/70 leading-relaxed mb-10">
            Participation is subject to applicable laws, regulations and requirements in the member's jurisdiction.
          </p>

          {/* Feature Pills */}
          <div className="grid grid-cols-2 gap-3 mb-8">
            {[
              { icon: Users, label: 'Community Network', sub: 'Collective participation' },
              { icon: TrendingUp, label: 'Structured Growth', sub: 'Binary rewards system' },
              { icon: Shield, label: 'Secure Platform', sub: 'Enterprise-grade security' },
              { icon: Star, label: 'Elite Plans', sub: 'R1,000 → R512,000' },
            ]?.map((feat) => (
              <div key={`feat-${feat?.label}`} className="glass-card rounded-2xl p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gold/10 flex items-center justify-center shrink-0">
                  <feat.icon className="w-4 h-4 text-gold" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{feat?.label}</p>
                  <p className="text-xs text-muted-foreground">{feat?.sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Real DB-backed stats — only shown when data is loaded */}
          {publicStats !== null && (
            <div className="grid grid-cols-2 gap-3 mb-8">
              <div className="glass-card rounded-2xl p-4 text-center">
                <p className="text-2xl font-bold text-gold tabular-nums">
                  {publicStats.active_members.toLocaleString('en-ZA')}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Active Members</p>
              </div>
              <div className="glass-card rounded-2xl p-4 text-center">
                <p className="text-2xl font-bold text-gold tabular-nums">
                  {formatNetworkValue(publicStats.network_value)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Verified Network Value</p>
              </div>
            </div>
          )}

          {/* Contact */}
          <div className="glass-card rounded-2xl p-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">General Support</p>
            <p className="text-sm text-muted-foreground">
              <span className="text-foreground font-medium">mbgnexus@gmail.com</span>
            </p>
          </div>
        </div>

        {/* Bottom */}
        <div className="relative z-10">
          <p className="text-xs text-muted-foreground/60">© MBG Group. All rights reserved.</p>
          <p className="text-[10px] text-muted-foreground/40 mt-1">
            MBG Nexus Pre Launch Programme v1.0 • Early Access Environment. Verified accounts and approved programme data will be securely carried forward into the official production release.
          </p>
        </div>
      </div>

      {/* Right Panel — Auth Forms */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <AppLogo size={36} />
            <span className="font-bold text-xl text-foreground">MBG Nexus</span>
          </div>

          {/* Tab Switcher */}
          <div className="flex bg-surface-2 rounded-2xl p-1 mb-8">
            <button
              onClick={() => setActiveTab('login')}
              className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${
                activeTab === 'login' ? 'gold-gradient text-primary-foreground shadow-lg' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setActiveTab('register')}
              className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${
                activeTab === 'register' ? 'gold-gradient text-primary-foreground shadow-lg' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Join Network
            </button>
          </div>

          {/* Forms */}
          <div className="animate-fade-in">
            {activeTab === 'login' ? (
              <LoginForm onSwitchToRegister={() => setActiveTab('register')} />
            ) : (
              <RegisterForm onSwitchToLogin={() => setActiveTab('login')} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
